<script setup>
/**
 * The shell: one navigation bar over three pages.
 *
 * The bar is what makes the split legible. Before, there was one page and no
 * way to tell who it was for; now the first thing a reader sees is which of the
 * three questions they are looking at the answer to.
 */
import { computed } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { ROUTES, EXTERNAL_PAGES } from './router.js'
import { useDrawer } from './composables/useDrawer.js'
import ThemeDrawer from './components/ui/ThemeDrawer.vue'
import LabMark from './components/ui/LabMark.vue'
import { offersDrawer } from './lib/customise.js'
import { t } from './lib/i18n.js'

const drawer = useDrawer()
const route = useRoute()

/**
 * Which pages get the shared navigation bar.
 *
 * The Overview and the Trend page draw their own header instead: both are
 * shown to people outside the team, and a row of internal tabs across the top
 * reads as somebody's admin tool rather than as a statement about the product.
 * The Overview puts its links in the footer; the Trend page keeps them in its
 * own strip. Only the engineering page uses the shared bar.
 *
 * Keyed on `path`, not on `name`: the pages are lazy chunks and the name is
 * undefined until the route resolves, so a name test renders the bar first and
 * removes it a moment later.
 */
const OWN_HEADER = ['/', '/progress', '/diagnostics']
const chrome = computed(() => !OWN_HEADER.includes(route.path))
</script>

<template>
  <a class="skip" href="#main">{{ t('Skip to content') }}</a>

  <header v-if="chrome" class="bar">
    <div class="bar-in">
      <RouterLink to="/" class="brand">
        <LabMark class="mark" />
        <span class="name">Tripla <b>Performance Lab</b></span>
      </RouterLink>

      <nav class="tabs" :aria-label="t('Dashboards')">
        <RouterLink
          v-for="page in ROUTES"
          :key="page.name"
          :to="{ name: page.name }"
          class="tab"
          active-class="on"
          >{{ t(page.meta.nav) }}</RouterLink
        >
        <a v-for="p in EXTERNAL_PAGES" :key="p.href" :href="p.href" class="tab">{{ t(p.nav) }}</a>
      </nav>

      <button
        type="button"
        class="ghost"
        aria-haspopup="dialog"
        :aria-expanded="drawer.open.value"
        @click="drawer.show()"
      >
        {{ t('Customise') }}
      </button>
    </div>
  </header>

  <main id="main">
    <!-- Keyed on the route name only, NOT on the full path: a window or a modal
         is a query change on the same route, and remounting the page for one
         would throw away every chart and refetch everything the reader was
         already looking at. That is the dead-end behaviour this redesign
         exists to remove. -->
    <RouterView v-slot="{ Component, route: current }">
      <component :is="Component" :key="current.name" />
    </RouterView>
  </main>

  <!-- Only where the page offers something (lib/customise.js): Back from
       Progress with the drawer open must not land on the Overview with it open. -->
  <ThemeDrawer v-if="drawer.open.value && offersDrawer(route.path)" @close="drawer.hide()" />
</template>

<style scoped>
.skip {
  position: absolute;
  left: -9999px;
  top: 0;
  z-index: 100;
  background: var(--surface);
  padding: 10px 14px;
  border-radius: 0 0 var(--r-sm) 0;
}

.skip:focus {
  left: 0;
}

.bar {
  position: sticky;
  top: 0;
  z-index: 30;
  background: color-mix(in srgb, var(--bg) 86%, transparent);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--line);
}

.bar-in {
  max-width: 1120px;
  margin: 0 auto;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 18px;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  text-decoration: none;
  color: var(--ink);
  flex: 0 0 auto;
}

/* The same mark as SiteHeader's (components/ui/LabMark.vue). */
.mark {
  width: 24px;
  height: 24px;
}

.name {
  font-size: 14px;
  color: var(--ink3);
  white-space: nowrap;
}
.name b {
  color: var(--ink);
  font-weight: 650;
}

.tabs {
  display: flex;
  gap: 2px;
  margin-left: auto;
  flex-wrap: wrap;
}

.tab {
  padding: 6px 12px;
  border-radius: var(--r-sm);
  font-size: 13.5px;
  color: var(--ink3);
  text-decoration: none;
  white-space: nowrap;
  transition:
    color var(--fast) var(--ease),
    background-color var(--fast) var(--ease);
}

.tab:hover {
  color: var(--ink);
  background: color-mix(in srgb, var(--accent) 10%, transparent);
}

.tab.on {
  color: var(--ink);
  background: color-mix(in srgb, var(--accent) 16%, transparent);
  font-weight: 600;
}

.ghost {
  background: none;
  border: 1px solid var(--line);
  color: var(--ink2);
  font-size: 13px;
  padding: 5px 11px;
  flex: 0 0 auto;
}

.ghost:hover {
  color: var(--ink);
  border-color: var(--accent);
}

@media (max-width: 720px) {
  .name {
    display: none;
  }
  .tabs {
    margin-left: 0;
  }
  .bar-in {
    gap: 10px;
  }
}
</style>
